import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import Logo from '@/components/generals/logo'
import { NAV_LINKS } from '@/constants/landing'

const DESKTOP_QUERY = '(min-width: 64rem)'

const FOCUS_RING = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground'

const SiteHeader: FC = (): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const restoreFocusRef = useRef(true)

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 8)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!isOpen) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow
        const menuButton = menuButtonRef.current
        const desktop = window.matchMedia(DESKTOP_QUERY)

        root.style.overflow = 'hidden'
        restoreFocusRef.current = true
        closeButtonRef.current?.focus()

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))

            if (focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }

            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        const handleBreakpoint = () => {
            if (desktop.matches) setIsOpen(false)
        }

        document.addEventListener('keydown', handleKeyDown)
        desktop.addEventListener('change', handleBreakpoint)

        return () => {
            root.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
            desktop.removeEventListener('change', handleBreakpoint)

            if (restoreFocusRef.current) menuButton?.focus({ preventScroll: true })
        }
    }, [isOpen])

    // Menu links jump to a section, so unlock scrolling right away and let the browser scroll there.
    const handleMenuLinkClick = useCallback(() => {
        restoreFocusRef.current = false
        document.documentElement.style.overflow = ''
        setIsOpen(false)
    }, [])

    return (
        <>
            <header
                className={cn(
                    'sticky top-0 z-40 border-b bg-background/85 backdrop-blur-md transition-colors duration-300',
                    isScrolled ? 'border-foreground/15' : 'border-transparent'
                )}
            >
                <div className='site-container flex h-16 items-center justify-between gap-6 md:h-20'>
                    <Link
                        href='/'
                        aria-label='Wallop Studio home'
                        className={cn('group/logo rounded-full', FOCUS_RING)}
                    >
                        <Logo />
                    </Link>

                    <nav aria-label='Main' className='hidden lg:block'>
                        <ul className='flex items-center gap-1'>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        className={cn(
                                            'inline-flex h-10 items-center rounded-full px-4 text-sm font-bold tracking-[0.06em] uppercase transition-colors hover:bg-foreground hover:text-background',
                                            FOCUS_RING
                                        )}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2'>
                        <a
                            href='/#contact'
                            className={buttonStyles({ size: 'sm', className: 'hidden sm:inline-flex' })}
                        >
                            Start a project
                            <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:rotate-45' />
                        </a>

                        <button
                            ref={menuButtonRef}
                            type='button'
                            onClick={() => setIsOpen(true)}
                            aria-expanded={isOpen}
                            aria-controls='site-menu'
                            aria-label='Open menu'
                            className={cn(
                                'grid size-11 cursor-pointer place-items-center rounded-full border-2 border-foreground transition-colors hover:bg-foreground hover:text-background lg:hidden',
                                FOCUS_RING
                            )}
                        >
                            <Menu aria-hidden='true' className='size-5' />
                        </button>
                    </div>
                </div>
            </header>

            {isOpen && (
                <div
                    ref={panelRef}
                    id='site-menu'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Site menu'
                    className='fixed inset-0 z-50 flex flex-col overflow-y-auto bg-primary text-primary-foreground motion-safe:animate-in fade-in slide-in-from-top-6 duration-300 ease-out lg:hidden'
                >
                    <div className='site-container flex h-16 shrink-0 items-center justify-between gap-6 md:h-20'>
                        <Link
                            href='/'
                            onClick={handleMenuLinkClick}
                            aria-label='Wallop Studio home'
                            className={cn('group/logo rounded-full', FOCUS_RING)}
                        >
                            <Logo burstClassName='text-foreground' />
                        </Link>

                        <button
                            ref={closeButtonRef}
                            type='button'
                            onClick={() => setIsOpen(false)}
                            aria-label='Close menu'
                            className={cn(
                                'grid size-11 cursor-pointer place-items-center rounded-full bg-foreground text-background transition-colors hover:bg-background hover:text-foreground',
                                FOCUS_RING
                            )}
                        >
                            <X aria-hidden='true' className='size-5' />
                        </button>
                    </div>

                    <nav aria-label='Menu' className='site-container flex flex-1 flex-col justify-center py-8'>
                        <ul className='border-t-2 border-foreground'>
                            {NAV_LINKS.map((link, index) => (
                                <li key={link.href} className='border-b-2 border-foreground'>
                                    <a
                                        href={link.href}
                                        onClick={handleMenuLinkClick}
                                        className={cn(
                                            'flex items-center justify-between gap-4 py-3 font-heading text-[clamp(3rem,15vw,6.5rem)] leading-[0.95] font-black uppercase wdth-75 transition-[font-variation-settings] duration-300 hover:wdth-100',
                                            FOCUS_RING
                                        )}
                                    >
                                        {link.label}

                                        <span className='font-mono text-sm font-medium tracking-normal'>
                                            0{index + 1}
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='site-container shrink-0 pb-8'>
                        <a
                            href='/#contact'
                            onClick={handleMenuLinkClick}
                            className={buttonStyles({ variant: 'ink', size: 'lg', className: 'w-full hover:bg-background hover:text-foreground' })}
                        >
                            Start a project
                            <ArrowUpRight aria-hidden='true' className='size-5' />
                        </a>

                        <p className='mt-5 text-center font-mono text-xs tracking-[0.18em] uppercase'>
                            Brooklyn, NY · Booking spring 2027
                        </p>
                    </div>
                </div>
            )}
        </>
    )
}

export default SiteHeader
